import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { setDateFormatFromString } from '@sd-portal/shared/functions';
import { BuildTripViewType } from '@sd-portal/shared/models';
import { getBATRoute } from '@sd-portal/shared/navigation';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';
import { AnalyticsService } from '@sd-portal/shared/util';

import { UserConfigurationFacade } from 'libs/shared/user-configuration/src/lib/+state/user-configuration.facade';
import { AppSearchFacade } from './app-search.facade';
import { AppSearchService, FullTextTripSearchResultItem, FullTextTripSearchResultItemICAO } from './app-search.service';

@Component({
  selector: 'sd-portal-app-search',
  templateUrl: './app-search.component.html',
  styleUrls: ['./app-search.component.scss'],
  providers: [AnalyticsService]
})
export class AppSearchComponent implements OnInit, OnDestroy {
  searchText: string;
  trips: FullTextTripSearchResultItem[];
  startDateTime: string;
  endDateTime: string;
  autoCompleteOpened: boolean;
  customerId: string | null;
  buildTripViewTypeId: BuildTripViewType | null;
  batRoute: string = '/pre-flight/schedule';

  showSearch$ = this.appSearchService.showSearch$;
  focusElement: string;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger })
  searchAutoComplete: MatAutocompleteTrigger;
  @ViewChild('pickerStartDate') pickerStartDate: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  private wasInside = false;
  private searchSubject = new Subject<{
    searchText: string;
    startDateTime: string;
    endDateTime: string;
  }>();

  @HostListener('click')
  clickInside() {
    this.wasInside = true;
  }

  @HostListener('document:click')
  clickout() {
    if (!this.wasInside) {
      this.autoCompleteOpened = false;
    }
    this.wasInside = false;
  }

  get isPreFlight(): boolean {
    return this.appSearchService.getSearchContext() === 'pre-flight';
  }

  constructor(
    private readonly router: Router,
    readonly appSearchService: AppSearchService,
    private readonly appSearchFacade: AppSearchFacade,
    private readonly userConfigurationFacade: UserConfigurationFacade,
    private readonly analyticsService: AnalyticsService
  ) {
  }

  ngOnInit() {
    // routing event listner
    this.router.events.pipe(untilViewDestroyed(this)).subscribe(() => {
      this.searchText = '';
      this.startDateTime = '';
      this.endDateTime = '';
      this.trips = [];
    });

    this.userConfigurationFacade.calendarConfiguration$
      .pipe(untilViewDestroyed(this))
      .subscribe(val => {
        if (val) {
          this.buildTripViewTypeId = val.buildTripViewTypeId;
        }
      });

    this.searchSubject
      .pipe(
        switchMap(({ searchText, startDateTime, endDateTime }) => {
          const isPostFlight = this.appSearchService.getSearchContext() === 'post-flight';
          if (isPostFlight) {
            return this.appSearchFacade.getFullTextTripSearch(searchText, startDateTime, endDateTime);
          } else {
            return this.appSearchFacade.getCalendarTripSearch(searchText, startDateTime, endDateTime);
          }
        }),
        untilViewDestroyed(this)
      )
      .subscribe(res => {
        this.trips = res as FullTextTripSearchResultItem[];
        setTimeout(() => {
          this.autoCompleteOpened = true;
        });
      });
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

  toggleAutocomplete(autoCompleteOpened: boolean) {
    this.autoCompleteOpened = autoCompleteOpened;
  }

  openDatePicker() {
    const DATEPICKER_CONTENT = document.querySelector('.mat-datepicker-content');
    if (DATEPICKER_CONTENT) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (<any>DATEPICKER_CONTENT).parentElement.parentElement.addEventListener('click', () => {
        setTimeout(() => {
          this.autoCompleteOpened = true;
        });
      });
    }

    const DATEPICKER_BACKDROP = document.querySelector('.mat-datepicker-4-backdrop');
    if (DATEPICKER_BACKDROP) {
      DATEPICKER_BACKDROP.addEventListener('click', () => {
        setTimeout(() => {
          this.autoCompleteOpened = true;
        });
      });
    }
  }

  validateDateInput(input: string): boolean {
    if (input === null) {
      return true;
    }
    const dateToValidate = new Date(input);
    return !isNaN(dateToValidate.getTime());
  }

  preventAutoCompleteClose(focusElement: string) {
    this.searchAutoComplete.openPanel();
    if (focusElement) {
      this.focusElement = focusElement;
    }
  }

  matDatePickerOptionClick() {
    this.searchAutoComplete.openPanel();
    if (this.focusElement) {
      document.getElementsByName(this.focusElement)[0].focus();
    }
  }

  searchTrip() {
    if (this.searchText != null && this.searchText.trim().length > 0) {
      const startDateTime = this.validateDateInput(this.startDateTime)
        ? setDateFormatFromString(this.startDateTime)
        : '';
      const endDateTime = this.validateDateInput(this.endDateTime)
        ? setDateFormatFromString(this.endDateTime)
        : '';

      this.searchSubject.next({
        searchText: this.searchText,
        startDateTime,
        endDateTime
      });
    } else {
      this.trips = [];
    }
  }

  sendClickEvent() {
    this.analyticsService.clickButton('Top search bar');
  }

  goToTrip(trip: FullTextTripSearchResultItem) {
    this.autoCompleteOpened = false;
    if (this.buildTripViewTypeId) {
      this.batRoute = getBATRoute(this.buildTripViewTypeId);
    }

    this.router.navigate([this.batRoute, trip.scheduledAircraftTripId]);
  }

  onTripRowClick(trip: FullTextTripSearchResultItem) {
    if (!this.isPreFlight) {
      return;
    }

    this.goToTrip(trip);
  }


  onTripIcaoClick(icao: FullTextTripSearchResultItemICAO) {
    if (this.isPreFlight) {
      return;
    }
    
    if (icao.postedFlightId) {
      this.router.navigate(['/post-flight/leg/edit', icao.postedFlightId]);
    } else if (icao.scheduledFlightId) {
      this.router.navigate(['/post-flight/post-scheduled/leg', icao.scheduledFlightId]);
    }

    this.autoCompleteOpened = false;
  }
}
